import { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { FPS } from '../../../constants'
import { useCreationDrawLoop } from '../../../hooks/use-creation-draw-loop'
import { CreationToolSourceFile } from '../../../types'
import { SourceEditorEdits } from '../../source-editor/types'

const Canvas = styled.canvas`
    display: block;
    width: 100%;
`

type Props = {
    className?: string
    source: CreationToolSourceFile
    edits?: SourceEditorEdits
    isPlaying?: boolean
    onTimeUpdate?: (currentTime: number) => void
}

const CreationSourcePlayerCanvas = ({ className, source, edits, isPlaying = true, onTimeUpdate }: Props) => {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const drawCount = useCreationDrawLoop(FPS)

    useEffect(() => {
        if (!isPlaying || !canvasRef.current) return

        const sourceDuration = source.duration || Math.floor(source.frames.length / FPS)
        const canvas = canvasRef.current
        const context = canvas.getContext('2d')
        const startTime = edits?.trim?.start || 0
        const endTime = edits?.trim?.end || sourceDuration
        const totalFrames = source.frames.map((f) => f.frameCount || 1).reduce((a, b) => a + b, 0)
        const startFrame = Math.floor((startTime / sourceDuration) * totalFrames)
        const endFrame = Math.floor((endTime / sourceDuration) * totalFrames)
        const currentFrame = startFrame + (drawCount % (endFrame - startFrame))
        const frameIndexes: number[] = []

        source.frames.forEach((frame, index) => {
            for (let i = 0; i < (frame.frameCount || 1); i++) {
                frameIndexes.push(index)
            }
        })

        if (currentFrame) {
            const frameIndex = frameIndexes[currentFrame]
            const { canvas: frameCanvas } = source.frames[frameIndex]
            canvas.width = frameCanvas.width
            canvas.height = frameCanvas.height

            context?.drawImage(frameCanvas, 0, 0)

            if (edits?.frames?.length) {
                const watermarkFrame = currentFrame % edits.frames.length
                const { canvas: watermarkCanvas } = edits.frames[watermarkFrame]
                context?.drawImage(watermarkCanvas, edits.crop?.x || 0, edits.crop?.y || 0)
            }

            onTimeUpdate?.(((currentFrame + 1) / totalFrames) * sourceDuration)
        }
    }, [canvasRef, drawCount, isPlaying, onTimeUpdate])

    return <Canvas ref={canvasRef} className={className} />
}

export default CreationSourcePlayerCanvas
